import {
  IMessage,
  mapMessageToPreviewData,
  MESSAGE_CONTENT_TYPE,
} from "@home-health-notify/domain";
import SpeakerNotesOffIcon from "@mui/icons-material/SpeakerNotesOff";
import { MessagePanelContext, useTranslation } from "hhn-commons";
import { MouseEvent, useContext } from "react";
import DisplayImage from "../../common/DisplayImage";
import { DocumentMediaViewerContext } from "../../context/DocumentMediaViewerContext";
import { IMessageTypeBubble } from "../MessagePanel";
import {
  BodyComponent,
  HeaderComponent,
  HeaderWrapper,
  ImageContainer,
  NoReplyContent,
  ReplyBubbleWrapper,
  TitleComponent,
  VerticalDivider,
} from "./ReplyBubble.style";
import { UserTitle } from "../../common/user/UserText";

export const ReplyBubble = (props: IMessageTypeBubble) => {
  const messageData = props.message.getMessage();
  const isReplyToExist = messageData.replyTo && messageData.replyTo.id;
  return (
    <ReplyBubbleWrapper
      data-testid="reply-message-bubble"
      isReplyPreview={props.isReplyPreview}
    >
      {!isReplyToExist && <NoReplyHeaderComponent />}
      {isReplyToExist && (
        <ReplyBubbleHeaderComponent
          message={props.message}
          isReply={props.isReply}
          isReplyPreview={props.isReplyPreview}
          isForwardOn={props.isForwardOn}
        ></ReplyBubbleHeaderComponent>
      )}
      {props.children}
    </ReplyBubbleWrapper>
  );
};

export const ReplyBubbleHeaderComponent = ({
  message,
  isReply,
  isReplyPreview = false,
  isForwardOn = false,
}: {
  message: IMessage;
  isReply?: boolean;
  isReplyPreview?: boolean;
  isForwardOn?: boolean;
}) => {
  const { setFocusedMessage } = useContext(MessagePanelContext);
  const { openDocumentMediaViewer } = useContext(DocumentMediaViewerContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const replyToMessage = isReply
    ? message.getMessage()
    : message.getMessage().replyTo;

  const focusRepliedMessage = () => {
    const repliedMessage = message?.getMessage().replyTo;
    repliedMessage && setFocusedMessage(repliedMessage.id);
  };

  const replyToMedia = replyToMessage
    ? mapMessageToPreviewData(replyToMessage)
    : undefined;
  const thumbnail =
    replyToMedia?.media[0]?.thumbnail || replyToMedia?.media[0]?.url || null;
  const caption = replyToMedia?.media[0]?.caption || "";
  const hideThumbnail =
    replyToMedia?.mediaType === MESSAGE_CONTENT_TYPE.DOCUMENT ||
    replyToMedia?.mediaType === MESSAGE_CONTENT_TYPE.VOICE;

  const openRepliedMedia = (event: MouseEvent) => {
    if (isForwardOn) return;
    event.stopPropagation();
    replyToMedia && openDocumentMediaViewer?.(replyToMedia, [replyToMedia]);
  };

  return (
    <HeaderWrapper
      onClick={focusRepliedMessage}
      data-testid="reply-bubble-header"
      isReplyPreview={isReplyPreview}
    >
      <VerticalDivider />
      {!hideThumbnail && thumbnail && (
        <ImageContainer data-testid="thumbnail" onClick={openRepliedMedia}>
          <DisplayImage
            url={thumbnail}
            alt={"thumbnail"}
            showLoader={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </ImageContainer>
      )}
      <HeaderComponent>
        {replyToMessage && (
          <>
            {replyToMessage?.isSender ? (
              <TitleComponent>{tNs("message.you")}</TitleComponent>
            ) : (
              <UserTitle userId={replyToMessage.from} />
            )}
          </>
        )}
        <BodyComponent>
          {replyToMessage?.body}&nbsp;
          {caption}
        </BodyComponent>
      </HeaderComponent>
    </HeaderWrapper>
  );
};

const NoReplyHeaderComponent = () => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  return (
    <HeaderWrapper data-testid="reply-bubble-header" isReplyPreview={true}>
      <VerticalDivider />
      <NoReplyContent>
        <SpeakerNotesOffIcon fontSize="small" color="secondary" />
        <BodyComponent>{tNs("message.noReplyMessage")}</BodyComponent>
      </NoReplyContent>
    </HeaderWrapper>
  );
};
